import { Button, Status, StatusState, StatusVariant, theme } from '@ketch-com/deck'
import { GetConsentStatusResponseBodySubscriptionsItemDTO } from '@ketch-com/figurehead'
import { Box, styled, Typography } from '@mui/material'
import { AuditLogActionVariant } from 'pages/insights/auditLog/components/AuditLogPopup'
import { HistoricalPermitPopupVariant } from 'pages/insights/auditLog/components/historicalPermitPopup/constants'
import { ChannelAccordion } from 'pages/insights/auditLog/components/subscriptionsTab/components/TopicItem/components/ChannelAccordion/ChannelAccordion'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import React from 'react'
import { formatDateTimeFromUnix } from 'utils/helpers/dateTime'

type TopicItemProps = {
  channels: GetConsentStatusResponseBodySubscriptionsItemDTO[]
}

const TopicItemBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${theme.palette.Common.Divider}`,
  borderRadius: '11px',
  padding: '8px 16px',
})

const TopicHeaderBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 0px 0px 18px',
  marginBottom: '16px',
})

const TopicHeaderLeftBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
})

const TopicHeaderRightBox = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  height: 'fit-content',
})

export const TopicItem: React.FC<TopicItemProps> = ({ channels }) => {
  const { triggerAuditLogPopup, setAuditLogHistoricalPermitPopupVariant, setAuditLogHistoricalPermitSubscriptionCode } =
    useAuditLogContext()

  if (!channels.length) return null

  // All channels have the same name and code
  const { name, code } = channels[0]

  // Get the last updated time across all channels
  const lastUpdated = channels.reduce((acc, channel) => {
    if (channel.collectedAt && channel.collectedAt > acc) {
      return channel.collectedAt
    }
    return acc
  }, 0)

  // Allowed is true if any channel is allowed
  const allowed = channels.some(channel => channel.allowed)

  return (
    <TopicItemBox>
      <TopicHeaderBox>
        <TopicHeaderLeftBox
          sx={{
            '& .MuiChip-labelSmall': {
              fontSize: '14px !important',
              fontWeight: '600 !important',
            },
          }}
        >
          <Typography variant="h4" mb="2px">
            {name}
          </Typography>
          <Typography variant="smallBody" color={theme.palette.Text.Secondary} mb="12px">
            {code}
          </Typography>
          <Box width="fit-content">
            <Status
              icon={allowed ? 'OCheck' : 'OCross'}
              status={allowed ? StatusState.success : StatusState.error}
              label={allowed ? 'Subscribed' : 'Unsubscribed'}
              variant={StatusVariant.filled}
            />
          </Box>
        </TopicHeaderLeftBox>
        <TopicHeaderRightBox>
          <Typography variant="smallBody" color={theme.palette.Text.Secondary} mr="12px">
            Last Updated: {formatDateTimeFromUnix(lastUpdated)}
          </Typography>
          <Button
            color="tertiary"
            onClick={() => {
              if (code) {
                setAuditLogHistoricalPermitPopupVariant(HistoricalPermitPopupVariant.Subscription)
                setAuditLogHistoricalPermitSubscriptionCode(code)
              }
            }}
          >
            View History
          </Button>
          <Button
            color="secondary"
            onClick={() => {
              triggerAuditLogPopup({ variant: AuditLogActionVariant.ManageSingleSubscription, subscriptionCode: code })
            }}
          >
            Manage
          </Button>
        </TopicHeaderRightBox>
      </TopicHeaderBox>
      {channels.map(channel => (
        <ChannelAccordion channel={channel} key={`${channel.code}-${channel.contactMethod}`} />
      ))}
    </TopicItemBox>
  )
}
